import {QueryClient} from '@tanstack/react-query'
import {
  DiagnosticPayloadType,
  EndpointAssociatedUserType,
} from './Endpoints.types'
import {
  ActivityByTimeRequest,
  AnalyticsSearchPayloadType,
  GetTopTenBlocksAction,
  ProductivityRequest,
  ProductivitySearchInputState,
  ShadowITViews,
} from './Analytics'
import {PoliciesClashesPayloadInterface, PolicyNamePayloadType} from './Policy'
import {CASBProviderType, CASBReviewFilesRequest} from './CASB.types'

export interface APIConfigType {
  isReactQuery: boolean
  queryKey: string[]
  queryFnName: string
  id: string
  reqPermissions: string[]
}

export interface AdditionalQueryClientParamsType {
  [key: string]: Record<string, unknown>
}

export enum PrefetchContextEnum {
  MOUNT = 'MOUNT',
}

export enum PrefetchQueryFnEnum {
  WARM_ENDPOINT_DIAGNOSTICS = 'warmEndpointDiagnostics',
  WARM_ASSOCIATED_USERS = 'warmAssociatedUsers',
  FETCH_POLICIES_DATA = 'fetchPoliciesData',
  FETCH_ENDPOINTS_DATA = 'fetchEndpointsData',
  FETCH_ENDPOINTS_METADATA = 'fetchEndpointsMetaData',
  FETCH_ANALYTICS_POLICIES_7DAY_TREND_DATA = 'fetchAnalyticsPolicies7DayTrendData',
  FETCH_ANALYTICS_POLICIES_TOP_BLOCKS_DATA = 'fetchAnalyticsPoliciesTopBlocksData',
  FETCH_ANALYTICS_PRODUCTIVITY_DATA = 'fetchAnalyticsProductivityData',
  FETCH_ANALYTICS_PRODUCTIVITY_ACTIVITY_TIME_DATA = 'fetchAnalyticsProductivityActivityTimeData',
  FETCH_ANALYTICS_SHADOW_IT_TOP_APPS_DATA = 'fetchAnalyticsShadowITTopAppsData',
  FETCH_ANALYTICS_SHADOW_IT_STREAM_CHARTS_DATA = 'fetchAnalyticsShadowITStreamChartsData',
  FETCH_API_CREDS_DATA = 'fetchAPICredsData',
  FETCH_BILLING_DATA = 'fetchBillingData',
  FETCH_BLOCK_PAGES_DATA = 'fetchBlockPagesData',
  FETCH_TENANT_CONFIG_DATA = 'fetchTenantConfigData',
  FETCH_SIEM_HTTP_DATA = 'fetchSiemHttpData',
  FETCH_SIEM_S3_DATA = 'fetchSiemS3Data',
  WARM_ANALYTICS_SEARCH = 'warmAnalyticsSearch',
  FETCH_SETTINGS_GENERAL_DATA = 'fetchSettingsGeneralData',
  WARM_POLICY_CATEGORIES = 'warmPolicyCategories',
  WARM_POLICY_BLOCK_PAGES = 'warmPolicyBlockPages',
  WARM_POLICY_ASSIGNMENT = 'warmPolicyAssignment',
  WARM_POLICY_CAC_APP = 'warmPolicyCACApp',
  WARM_POLICY_BYPASS = 'warmPolicyBypass',
  FETCH_CASB_APPLICATIONS_DATA = 'fetchCASBApplicationsData',
  FETCH_CASB_OVERVIEW_DATA = 'fetchCASBOverviewData',
  FETCH_CASB_FILES_DATA = 'fetchCASBFilesData',
  FETCH_CASB_FILE_DETAILS = 'fetchCASBFileDetails',
}

export interface ContextualPrefetchType {
  route: string
  context: string
  payload?: Record<string, unknown>
  /** Optional prefetch handlers to skip for a given contextual call. */
  skipQueryFnNames?: PrefetchQueryFnEnum[]
}

export interface ReactQueryFetchFunctionMapType {
  fetchPoliciesData: (queryClient: QueryClient) => Promise<void>
  fetchCASBApplicationsData: (queryClient: QueryClient) => Promise<void>
  fetchCASBOverviewData: (
    queryClient: QueryClient,
    params: CASBProviderType,
  ) => Promise<void>
  fetchCASBFilesData: (
    queryClient: QueryClient,
    params: CASBProviderType,
  ) => Promise<void>
  fetchEndpointsData: (queryClient: QueryClient) => Promise<void>
  fetchCASBFileDetails: (
    queryClient: QueryClient,
    queryKey: string[],
    payload: CASBReviewFilesRequest,
  ) => Promise<void>
  fetchEndpointsMetaData: (queryClient: QueryClient) => Promise<void>
  fetchAnalyticsPolicies7DayTrendData: (
    queryClient: QueryClient,
  ) => Promise<void>
  fetchAnalyticsPoliciesTopBlocksData: (
    queryClient: QueryClient,
    queryKey: string[],
    payload: GetTopTenBlocksAction,
  ) => Promise<void>
  fetchAnalyticsProductivityData: (
    queryClient: QueryClient,
    queryKey: string[],
    payload: ProductivityRequest,
  ) => Promise<void>
  fetchAnalyticsProductivityActivityTimeData: (
    queryClient: QueryClient,
    queryKey: string[],
    payload: ActivityByTimeRequest,
  ) => Promise<void>
  fetchAnalyticsShadowITTopAppsData: (
    queryClient: QueryClient,
    queryKey: string[],
    payload: {
      searchType: string
      searchTerm: string
    },
  ) => Promise<void>
  fetchAnalyticsShadowITStreamChartsData: (
    queryClient: QueryClient,
    queryKey: string[],
    payload: {
      shadowITView: ShadowITViews
      shadowITSearchQuery: ProductivitySearchInputState
    },
  ) => Promise<void>
  fetchSettingsGeneralData: (queryClient: QueryClient) => Promise<void>
  fetchAPICredsData: (queryClient: QueryClient) => Promise<void>
  fetchBillingData: (queryClient: QueryClient) => Promise<void>
  fetchBlockPagesData: (queryClient: QueryClient) => Promise<void>
  fetchTenantConfigData: (queryClient: QueryClient) => Promise<void>
  fetchSiemS3Data: (queryClient: QueryClient) => Promise<void>
  fetchSiemHttpData: (queryClient: QueryClient) => Promise<void>
}

export interface WarmUpFunctionMap {
  warmEndpointDiagnostics: (payload: DiagnosticPayloadType) => Promise<void>
  warmAssociatedUsers: (payload: EndpointAssociatedUserType) => Promise<void>
  warmAnalyticsSearch: (payload: AnalyticsSearchPayloadType) => Promise<void>
  warmPolicyCategories: (
    payload: PoliciesClashesPayloadInterface,
  ) => Promise<void>
  warmPolicyBlockPages: () => Promise<void>
  warmPolicyAssignment: (payload: PolicyNamePayloadType) => Promise<void>
  warmPolicyCACApp: (payload: PoliciesClashesPayloadInterface) => Promise<void>
  warmPolicyBypass: (payload: PoliciesClashesPayloadInterface) => Promise<void>
}
